import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { useProducts } from "@/hooks/useProducts";
import { euro } from "@/lib/api";
import { Reveal } from "@/components/site/Reveal";

function Row({ p, i, active, onEnter }) {
  return (
    <Link
      to={`/produit/${p.slug}`}
      data-testid={`signature-row-${p.slug}`}
      onMouseEnter={onEnter}
      className="group flex items-center gap-5 border-b border-[var(--line)] py-6 md:py-7"
    >
      <span className="w-8 text-xs text-[var(--gold)]">{String(i + 1).padStart(2, "0")}</span>
      <img src={p.image} alt={p.name} width="64" height="64" loading="lazy" className="h-16 w-16 shrink-0 rounded-sm object-cover md:hidden" />
      <span className={`flex-1 font-display text-2xl transition-[color,transform] duration-500 md:text-5xl ${active ? "translate-x-3 italic text-[var(--gold-soft)]" : "text-[var(--parchment)]"}`}>
        {p.name}
      </span>
      <span className="font-display text-xl text-[var(--gold-soft)] md:text-2xl">{euro(p.price)}</span>
    </Link>
  );
}

export function SignatureIndex() {
  const { data } = useProducts({ featured: true });
  const list = (Array.isArray(data) ? data : []).filter((p) => p.type === "pack").slice(0, 6);
  const [idx, setIdx] = useState(0);
  const cur = list[idx];
  if (!list.length) return null;

  return (
    <section data-testid="signature-index" className="relative bg-[var(--ink-2)] py-24 md:py-36">
      <div className="mx-auto grid max-w-[1440px] gap-14 px-5 md:px-10 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <Reveal>
            <p className="eyebrow mb-4">Les signatures</p>
            <h2 className="mb-12 font-display text-4xl leading-none md:text-6xl">Routines <span className="italic gold-text">iconiques.</span></h2>
          </Reveal>
          <div className="border-t border-[var(--line)]">
            {list.map((p, i) => <Row key={p.slug} p={p} i={i} active={i === idx} onEnter={() => setIdx(i)} />)}
          </div>
        </div>
        <div className="relative hidden lg:col-span-5 lg:block">
          <div className="sticky top-28 aspect-[4/5] overflow-hidden rounded-sm border border-[var(--line)] bg-[var(--ink)]">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_30%,rgba(217,180,106,0.22),transparent_60%)]" />
            <AnimatePresence initial={false}>
              <motion.img
                key={cur?.slug}
                src={cur?.image}
                alt={cur?.name}
                initial={{ clipPath: "inset(100% 0% 0% 0%)", scale: 1.15 }}
                animate={{ clipPath: "inset(0% 0% 0% 0%)", scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.9, ease: [0.76, 0, 0.24, 1] }}
                className="absolute inset-x-6 top-6 aspect-square w-[calc(100%-3rem)] rounded-sm object-contain drop-shadow-[0_30px_60px_rgba(0,0,0,0.6)]"
              />
            </AnimatePresence>
            <div className="absolute inset-x-6 bottom-6 flex items-end justify-between border-t border-[var(--line)] pt-5">
              <div>
                <p className="eyebrow">{cur?.badge}</p>
                <p className="mt-1 max-w-[260px] text-sm text-[var(--parchment)]/85">{cur?.tagline}</p>
              </div>
              {cur?.compare_at ? <span className="text-sm text-[var(--muted)] line-through">{euro(cur.compare_at)}</span> : null}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
